import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import React, { useEffect, useRef } from "react";

interface InteractiveProfileImageProps {
  src: string;
  backgroundSrc?: string; // Optional background layer
  alt: string;
  className?: string;
}

const InteractiveProfileImage: React.FC<InteractiveProfileImageProps> = ({
  src,
  backgroundSrc,
  alt,
  className = "",
}) => {
  const ref = useRef<HTMLDivElement>(null);

  // Motion values for tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const deviceOrientationX = useMotionValue(0);
  const deviceOrientationY = useMotionValue(0);

  // Smooth spring animations
  const springConfig = { damping: 100, stiffness: 100, mass: 30 };

  // Transform values for person layer - more subtle head movements
  const personRotateX = useSpring(
    useTransform(() => {
      const mouse = mouseY.get();
      const device = deviceOrientationY.get();
      return (mouse * 0.3 + device * 0.2); // Subtle head tilt up/down
    }),
    springConfig
  );

  const personRotateY = useSpring(
    useTransform(() => {
      const mouse = mouseX.get();
      const device = deviceOrientationX.get();
      return (mouse * 0.2 + device * 0.2); // Subtle head turn left/right
    }),
    springConfig
  );

  // Transform values for background layer - counter-movement for depth
  const backgroundRotateX = useSpring(
    useTransform(() => {
      const mouse = mouseY.get();
      const device = deviceOrientationY.get();
      return -(mouse * 0.1 + device * 0.05); // Counter-rotate background
    }),
    springConfig
  );

  const backgroundRotateY = useSpring(
    useTransform(() => {
      const mouse = mouseX.get();
      const device = deviceOrientationX.get();
      return -(mouse * 0.15 + device * 0.05); // Counter-rotate background
    }),
    springConfig
  );

  // Person layer translation for more realistic "looking"
  const personTranslateX = useSpring(
    useTransform(() => {
      const mouse = mouseX.get();
      return mouse * 0.001; // Slightly reduced horizontal shift
    }),
    springConfig
  );

  const personTranslateY = useSpring(
    useTransform(() => {
      const mouse = mouseY.get();
      return mouse * 0.02; // Slightly reduced vertical shift
    }),
    springConfig
  );

  // Global mouse tracking - always follows cursor anywhere on page
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!ref.current) return;

      const rect = ref.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate angle from image center to cursor position
      const deltaX = e.clientX - centerX;
      const deltaY = e.clientY - centerY;

      // Convert to rotation values with more range for global tracking
      const maxDistance = Math.sqrt(window.innerWidth ** 2 + window.innerHeight ** 2) / 3;
      const normalizedX = Math.max(-1, Math.min(1, deltaX / maxDistance));
      const normalizedY = Math.max(-1, Math.min(1, deltaY / maxDistance));

      mouseX.set(normalizedX * 35);
      mouseY.set(-normalizedY * 35);
    };

    // Always track mouse globally, not just on hover
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  // Device orientation tracking
  useEffect(() => {
    const handleOrientation = (e: DeviceOrientationEvent) => {
      if (e.gamma !== null && e.beta !== null) {
        const x = Math.max(-25, Math.min(25, e.gamma * 0.4));
        const y = Math.max(-25, Math.min(25, (e.beta - 90) * 0.3));

        deviceOrientationX.set(x);
        deviceOrientationY.set(y);
      }
    };

    const enableOrientation = async () => {
      if (typeof window !== 'undefined' && 'DeviceOrientationEvent' in window) {
        if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
          try {
            const permission = await (DeviceOrientationEvent as any).requestPermission();
            if (permission === 'granted') {
              window.addEventListener('deviceorientation', handleOrientation);
            }
          } catch (error) {
            console.log('Device orientation permission denied');
          }
        } else {
          window.addEventListener('deviceorientation', handleOrientation);
        }
      }
    };

    enableOrientation();
    return () => window.removeEventListener('deviceorientation', handleOrientation);
  }, [deviceOrientationX, deviceOrientationY]);

  return (
    <motion.div
      ref={ref}
      className={`relative ${className}`}
      style={{ perspective: "1200px" }}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="relative w-48 h-48 md:w-64 md:h-64 mx-auto">
        {/* Master clipping container - everything inside gets clipped to this circle */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            transformStyle: "preserve-3d",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)"
          }}
        >

          {!backgroundSrc && (
            <img
              src={src}
              alt={alt}
              className="absolute inset-0 w-full h-full object-cover object-center"
              draggable={false}
            />
          )}

          {/* Background Layer - shows first, behind everything */}
          {backgroundSrc && (
            <motion.div
              style={{
                rotateX: backgroundRotateX,
                rotateY: backgroundRotateY,
                transformStyle: "preserve-3d",
              }}
              className="absolute inset-0 transform-gpu"
            >
              <div
                className="absolute inset-0"
                style={{
                  transform: "translateZ(-20px) scale(1.1)",
                  filter: "blur(0.5px)",
                }}
              >
                <img
                  src={backgroundSrc}
                  alt="Background"
                  className="w-full h-full object-cover object-center opacity-50"
                  draggable={false}
                  style={{
                    backfaceVisibility: "hidden",
                  }}
                />
              </div>
            </motion.div>
          )}

          {/* Person Layer - moves freely but gets clipped by the master container */}
          {backgroundSrc && <motion.div
            style={{
              rotateX: personRotateX,
              rotateY: personRotateY,
              translateX: personTranslateX,
              translateY: personTranslateY,
              transformStyle: "preserve-3d",
            }}
            className="absolute inset-0 transform-gpu"
          >
            <div
              className="absolute inset-0"
              style={{
                transform: "translateZ(50px) scale(1.1)",
              }}
            >
              <img
                src={src}
                alt={alt}
                className="w-full h-full object-cover"
                draggable={false}
                style={{
                  backfaceVisibility: "hidden",
                  transform: "scale(1.1) translateY(12%)",
                  objectPosition: "center 20%",
                }}
              />
            </div>
          </motion.div>}

        </div>
      </div>
    </motion.div>
  );
};

export default InteractiveProfileImage;
